import type { SettingsTranslations } from '../interfaces/settings';

export const settingsTranslations: SettingsTranslations = {
  title_page: 'Settings',
  subtitle_page: 'Workspace and account preferences',
  nav_preferences: 'Preferences',
  nav_integrations: 'Integrations',
  nav_organization: 'Organization',
  nav_roles: 'Roles & policies',
  nav_billing: 'Plan & billing',
  settings_eyebrow: 'Settings',
  settings_group_account: 'Account',
  settings_group_integrations: 'Integrations',
  settings_group_organization: 'Organization',
  language_label: 'Language',
  general_settings_title: 'General',
  domain_settings_title: 'Domain',
  integrations_settings_title: 'Integrations',
  theme_label: 'Theme',
  theme_description: 'Switch between light and dark appearance.',
  theme_light: 'Light',
  theme_dark: 'Dark',
  language_description: 'Choose the language used across the dashboard.',

  appearance_section_title: 'Appearance',
  appearance_theme_card_title: 'Theme',
  appearance_theme_card_sub: 'Choose how the interface looks. System follows your OS preference.',
  appearance_theme_light_hint: 'Best for well-lit environments.',
  appearance_theme_dark_hint: 'Less glare, ideal for low-light environments.',
  appearance_theme_system_label: 'System',
  appearance_theme_system_hint: 'Follows your operating system light/dark mode.',

  language_eyebrow: 'Language',
  language_section_title: 'Interface language',
  language_section_description: 'Applies instantly across every label in the app.',
  language_interface_label: 'Language',
  language_interface_hint: 'Your choice is saved in this browser.',

  // Billing
  billing_title: 'Plan & billing',
  billing_subtitle: 'Manage your subscription, payment methods and invoices.',
  billing_current_plan_label: 'Current plan',
  billing_renews_on: 'Renews on {{date}}',
  billing_per_month: '/ month',
  billing_per_year: '/ year',
  billing_view_invoice: 'View invoice',
  billing_upgrade_plan: 'Upgrade plan',
  billing_no_subscription_title: "You don't have an active subscription",
  billing_no_subscription_subtitle:
    'Pick a plan to start using {{brand}} and unlock all channels and agents.',
  billing_choose_plan: 'Choose a plan',
  billing_loading: 'Loading...',
  billing_usage_of: 'of {{max}}',
  billing_usage_unlimited: 'Unlimited',
  billing_usage_disabled: 'Not included',
  billing_usage_per_month: '/ month',
  billing_feature_agents_active: 'Active agents',
  billing_feature_whatsapp_numbers: 'WhatsApp numbers',
  billing_feature_team_members: 'Team members',
  billing_feature_ai_messages_monthly: 'AI messages',
  billing_feature_whatsapp_conversations_monthly: 'Conversations',
  billing_payment_method_title: 'Payment method',
  billing_payment_method_coming_soon_title: 'Coming soon',
  billing_payment_method_coming_soon_description:
    "We're working on payment-gateway integration. For now, billing is handled manually by our team.",
  billing_payment_method_change: 'Change',
  billing_invoices_title: 'Invoice history',
  billing_invoices_empty_title: 'No invoices yet',
  billing_invoices_empty_description:
    'Payments tied to your subscription will appear here once they are generated.',
  billing_invoices_status_succeeded: 'Paid',
  billing_invoices_status_pending: 'Pending',
  billing_invoices_status_failed: 'Failed',
  billing_invoices_status_refunded: 'Refunded',
  billing_invoices_status_cancelled: 'Cancelled',
  billing_tax_data_title: 'Billing details',
  billing_tax_data_coming_soon_title: 'Coming soon',
  billing_tax_data_coming_soon_description:
    "You'll soon be able to save your tax details so they appear on your invoices.",
  billing_tax_data_legal_name: 'Legal name',
  billing_tax_data_tax_id: 'Tax ID',
  billing_tax_data_address: 'Address',
  billing_change_plan_dialog_title: 'Change plan',
  billing_change_plan_dialog_description:
    'Pick your new plan. The change is applied immediately and resets the billing period.',
  billing_change_plan_dialog_confirm: 'Confirm change',
  billing_change_plan_dialog_cancel: 'Cancel',
  billing_change_plan_billing_monthly: 'Monthly',
  billing_change_plan_billing_annual: 'Annual',
  billing_change_plan_current_badge: 'Current plan',
  billing_change_plan_recommended_badge: 'Recommended',
  billing_change_plan_popular_badge: 'Most popular',
  billing_change_plan_no_plans:
    'No plans are available right now. Contact us to help you choose one.',
  billing_change_plan_success_toast: 'Plan updated successfully.',
  billing_change_plan_error_toast: "We couldn't update the plan.",
  billing_change_plan_cta_current: 'Current plan',
  billing_change_plan_cta_choose: 'Switch to {{name}}',
  billing_change_plan_cta_contact: 'Talk to sales',
  billing_change_plan_features_title: 'Includes',
  billing_change_plan_billed_monthly_caption: 'billed monthly',
  billing_change_plan_billed_annual_caption: '{{total}} billed annually',
  billing_change_plan_custom_price: 'Custom',
  billing_change_plan_custom_price_caption: 'based on volume',
  billing_change_plan_unlimited_suffix: 'unlimited',
  billing_change_plan_per_month_suffix: '/ month',
  billing_change_plan_back: 'Back to overview',
  billing_change_plan_cta_continue: 'Continue with {{name}}',
  billing_change_plan_need_more: 'Need more?',
  billing_change_plan_need_more_hint: 'For high volume, SSO or compliance,',
  billing_change_plan_annual_savings: '−2 months',
  billing_change_plan_free_caption: 'no charge',
  billing_trial_cta_start: 'Start {{days}}-day free trial',
  billing_trial_cta_disabled_tooltip: 'Coming soon · contact sales to enable',
  billing_trial_days_free_badge: '{{days}} days free',

  billing_trial_title: "You're on a free trial",
  billing_trial_badge: 'No plan',
  billing_trial_subtitle:
    'You have {{daysLeft}} of {{daysTotal}} days left. Pick a plan so your agents keep answering after that.',
  billing_trial_progress_label: '{{daysLeft}} days left',
  billing_no_plan_title: "You don't have an active plan yet",
  billing_no_plan_badge: 'No plan',
  billing_no_plan_subtitle:
    'Activate a plan so your agents keep answering and unlock all channels.',

  billing_trial_usage_title: 'Your usage during the trial',
  billing_trial_usage_subtitle: 'Helps you estimate which plan you need.',
  billing_trial_usage_conversations: 'Conversations',
  billing_trial_usage_conversations_projection:
    '≈ {{projected}} / month projected',
  billing_trial_usage_agents: 'Agents created',
  billing_trial_usage_agents_caption: 'including drafts',
  billing_trial_usage_accounts: 'Connected accounts',
  billing_trial_usage_accounts_caption: 'across your channels',

  billing_faq_title: 'Before you choose',
  billing_faq_subtitle: 'The most common questions.',
  billing_faq_q1: 'What happens when my trial ends?',
  billing_faq_a1:
    'Your agents pause replies and we email you 3 days before. Your data and settings are kept for 90 days in case you reactivate.',
  billing_faq_q2: 'Can I change my plan later?',
  billing_faq_a2:
    'Yes. Upgrades are immediate and prorated. Downgrades apply at the end of the cycle. No penalty.',
  billing_faq_q3: 'Is there a discount for paying yearly?',
  billing_faq_a3: 'Yes — it works out to 2 free months over the monthly rate.',

  billing_checkout_back: 'Change plan',
  billing_checkout_confirm_title: 'Confirm your order',
  billing_checkout_confirm_subtitle:
    "Takes less than a minute. We'll only charge you when your trial ends.",
  billing_checkout_plan_label: 'Selected plan',
  billing_checkout_plan_chip_interval_monthly: 'Monthly',
  billing_checkout_plan_chip_interval_annual: 'Annual',
  billing_checkout_card_title: 'Payment method',
  billing_checkout_card_subtitle: "SSL-encrypted. We don't store your CVC.",
  billing_checkout_card_number: 'Card number',
  billing_checkout_card_expiry: 'Expiry',
  billing_checkout_card_cvc: 'CVC',
  billing_checkout_card_holder: 'Cardholder name',
  billing_checkout_tax_title: 'Billing details',
  billing_checkout_tax_legal_name: 'Legal name',
  billing_checkout_tax_id: 'Tax ID',
  billing_checkout_tax_address: 'Tax address',
  billing_checkout_summary_label: 'Summary',
  billing_checkout_summary_billing: 'Billing',
  billing_checkout_summary_subtotal: 'Plan {{name}}',
  billing_checkout_summary_tax: 'Tax (16%)',
  billing_checkout_summary_total_today: 'Total today',
  billing_checkout_pay_button: 'Pay and activate',
  billing_checkout_pay_processing: 'Processing…',
  billing_checkout_pay_disclaimer: "We won't charge you until your trial ends.",
  billing_checkout_guarantee_title: 'Cancel anytime',
  billing_checkout_guarantee_description:
    '30-day guarantee. Encrypted payment. Data on SOC2 + GDPR.',

  billing_done_label: 'Plan activated',
  billing_done_welcome: 'Welcome to {{name}}!',
  billing_done_renews_caption:
    '{{price}} · {{interval}} billing · renews on {{date}}',
  billing_done_view_invoice: 'View invoice',
  billing_done_next_steps_title: 'Next steps',
  billing_done_next_steps_subtitle:
    'Get the most out of your plan in less than 5 minutes.',
  billing_done_step_connect_title: 'Connect more accounts',
  billing_done_step_connect_desc:
    'Add more WhatsApp, Instagram or your website to multiply your reach.',
  billing_done_step_handoff_title: 'Enable human handoff',
  billing_done_step_handoff_desc:
    'For conversations that need a human touch or complex orders.',
  billing_done_step_webhook_title: 'Set up webhooks',
  billing_done_step_webhook_desc:
    'Receive events in your backend: qualified leads, closed sales, errors.',
  billing_done_go_to_overview: 'Go to overview',
  meta_capi_title: 'Meta Pixel · Conversions API',
  meta_capi_subtitle:
    'Attribute WhatsApp conversations to your Click-to-WhatsApp ads and measure real conversions in Meta Ads.',
  meta_capi_pixel_id_label: 'Pixel ID',
  meta_capi_access_token_label: 'Access token',
  meta_capi_access_token_set: 'Token saved',
  meta_capi_access_token_change: 'Change',
  meta_capi_test_event_code_label: 'Test event code (optional)',
  meta_capi_test_event_code_hint:
    'When set, events show up in the Events Manager "Test Events" tab.',
  meta_capi_enabled_label: 'Enabled',
  meta_capi_save: 'Save configuration',
  meta_capi_test_event: 'Send test event',
  meta_capi_test_event_sent:
    'Test event queued. Check the log below in a few seconds.',
  meta_capi_how_to_get_token: 'How do I get one?',
  meta_capi_how_to_get_token_body:
    'Meta Business Suite → Settings → Datasets → Pixel → Generate access token. You need pixel admin permissions.',
  meta_capi_events_title: 'Recent events',
  meta_capi_events_empty: 'No events sent yet.',
  meta_capi_status_queued: 'Queued',
  meta_capi_status_sent: 'Sent',
  meta_capi_status_failed: 'Retrying',
  meta_capi_status_dead: 'Failed',
  meta_capi_toast_saved: 'Configuration saved.',
  meta_capi_toast_error: 'Could not save. Please try again.',
  meta_capi_required_field: 'This field is required',
  // API keys
  nav_api_keys: 'API keys',
  nav_api_usage: 'API Usage',
  settings_group_developers: 'Developers',
  api_keys_eyebrow: 'Developers',
  api_keys_section_title: 'API keys',
  api_keys_section_description:
    'Create API keys for programmatic access and assign them one or more roles that define their permissions.',
  api_keys_new: 'New API key',
  api_keys_col_name: 'Name',
  api_keys_col_key: 'Key',
  api_keys_col_roles: 'Roles',
  api_keys_col_status: 'Status',
  api_keys_col_created: 'Created',
  api_keys_col_last_used: 'Last used',
  api_keys_status_active: 'Active',
  api_keys_status_disabled: 'Revoked',
  api_keys_never_used: 'Never',
  api_keys_empty_title: 'No API keys yet',
  api_keys_empty_description:
    'Create your first key to access the API with a role’s permissions.',
  api_keys_load_error: 'Could not load API keys',
  api_keys_action_edit_roles: 'Edit roles',
  api_keys_action_rotate: 'Rotate secret',
  api_keys_action_revoke: 'Revoke',
  api_keys_action_enable: 'Re-enable',
  api_keys_action_delete: 'Delete',
  api_keys_cancel: 'Cancel',
  api_keys_create_title: 'New API key',
  api_keys_create_description:
    'Name the key and pick the roles that will define its permissions.',
  api_keys_create_submit: 'Create API key',
  api_keys_name_label: 'Name',
  api_keys_name_placeholder: 'e.g. CI integration',
  api_keys_name_required: 'Name is required',
  api_keys_roles_label: 'Roles',
  api_keys_roles_description:
    'The key inherits the permissions of the selected roles.',
  api_keys_roles_placeholder: 'Select one or more roles',
  api_keys_roles_required: 'Select at least one role',
  api_keys_edit_roles_title: 'Edit roles',
  api_keys_edit_roles_description:
    'Update the roles associated with this API key.',
  api_keys_edit_roles_submit: 'Save',
  api_keys_col_cidrs: 'IP ranges',
  api_keys_cidrs_none: 'No restriction',
  api_keys_cidrs_count: '{{n}} range(s)',
  api_keys_action_edit_cidrs: 'Edit IP ranges',
  api_keys_cidrs_label: 'Allowed IP ranges (CIDR)',
  api_keys_cidrs_description:
    'Optional. If you add ranges, the key only works from IPs inside them. Empty = no restriction. Type a CIDR and press Enter.',
  api_keys_cidrs_placeholder: 'e.g. 190.85.0.0/16',
  api_keys_cidrs_invalid: 'Enter valid CIDR ranges (e.g. 10.0.0.0/8).',
  api_keys_edit_cidrs_title: 'Edit IP ranges',
  api_keys_edit_cidrs_description:
    'Restrict this API key to a list of CIDR ranges (IPv4/IPv6). Leave empty to allow any IP.',
  api_keys_edit_cidrs_submit: 'Save',
  api_keys_secret_title: 'Save your secret',
  api_keys_secret_description:
    'Copy these values now. The secret is shown only once and you won’t be able to see it again.',
  api_keys_secret_public_label: 'API key (x-api-key)',
  api_keys_secret_secret_label: 'Secret (x-secret)',
  api_keys_secret_warning:
    'The secret is not stored in plaintext. If you lose it, you’ll need to rotate it.',
  api_keys_secret_copy: 'Copy',
  api_keys_secret_copied: 'Copied',
  api_keys_secret_close: 'Done',
  api_keys_rotate_title: 'Rotate the secret?',
  api_keys_rotate_description:
    'A new secret will be generated and the current one will stop working immediately. Integrations using it will fail to authenticate.',
  api_keys_revoke_title: 'Revoke this API key?',
  api_keys_revoke_description:
    'The key will stop authenticating immediately. You can re-enable it later.',
  api_keys_enable_title: 'Re-enable this API key?',
  api_keys_enable_description: 'The key will authenticate requests again.',
  api_keys_delete_title: 'Delete this API key?',
  api_keys_delete_description: 'This action is permanent and cannot be undone.',
  api_keys_toast_created: 'API key created.',
  api_keys_toast_roles_updated: 'Roles updated.',
  api_keys_toast_cidrs_updated: 'IP ranges updated.',
  api_keys_toast_rotated: 'Secret rotated.',
  api_keys_toast_revoked: 'API key revoked.',
  api_keys_toast_enabled: 'API key re-enabled.',
  api_keys_toast_deleted: 'API key deleted.',
  api_keys_toast_error: 'Something went wrong. Please try again.',
  meta_ads_title: 'Meta Ads',
  meta_ads_subtitle: 'Connect your Meta ad accounts to see campaigns and ad metrics inside {{brand}}.',
  meta_ads_no_connection_title: 'No Meta connection',
  meta_ads_no_connection_body: 'Authorize {{brand}} with your Meta account to read your ad accounts. Read-only: we never create or change campaigns.',
  meta_ads_connect_button: 'Connect with Meta',
  meta_ads_badge_connected: 'Connected',
  meta_ads_badge_reauth: 'Needs reconnection',
  meta_ads_reauth_title: 'Authorization expired',
  meta_ads_reauth_body: 'Reconnect your Meta account so {{brand}} can keep reading your ads data.',
  meta_ads_expires_label: 'Authorization valid until',
  meta_ads_never_expires: 'Authorization does not expire',
  meta_ads_disconnect: 'Disconnect',
  meta_ads_accounts_title: 'Ad accounts',
  meta_ads_accounts_empty: 'No ad accounts linked yet. Load the available ones and pick which to read.',
  meta_ads_load_available: 'Load available accounts',
  meta_ads_available_title: 'Available in your Meta account',
  meta_ads_available_empty: 'Your Meta account has no visible ad accounts.',
  meta_ads_account_connect: 'Link',
  meta_ads_account_connected: 'Linked',
  meta_ads_not_covered: 'Not covered by token',
  meta_ads_account_status_active: 'Active',
  meta_ads_account_status_other: 'Check in Meta',
  meta_ads_toast_connected: 'Meta account connected.',
  meta_ads_toast_account_connected: 'Ad account linked.',
  meta_ads_toast_disconnected: 'Disconnected.',
  meta_ads_toast_cancelled: 'Connection cancelled.',
  meta_ads_toast_error: 'Something went wrong. Try again.',
  meta_ads_sdk_error: 'Could not load the Facebook SDK. Check your connection or ad blocker.',
  meta_ads_config_missing: 'The Meta Ads login configuration id is not set for this environment.',
};
